import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, of, shareReplay, tap, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserLoginResponseDTO } from '../dtos/account/login.model';
import { BrowserPlatformService } from '../../shared/services/browser-platform.service';
import { ListingFilterStateService } from '../../shared/services/listing-filter-state.service';
import { LaunchPhaseService } from './launch-phase.service';

const AUTH_STORAGE_KEY = 'ccms.auth';
const AUTH_TAB_HANDOFF_STORAGE_KEY = 'ccms.auth.tab-handoff';
const ACTIVITY_REFRESH_INTERVAL_MS = 15 * 60 * 1000;
const SESSION_REFRESH_TIMEOUT_MS = 15 * 1000;
const LIMITED_ADMIN_TOOLS_EMAIL = 'annemurray@carescertification.com';
const LIMITED_ADMIN_TOOL_ROUTES = new Set([
  '/admin-tools/users',
  '/admin-tools/sharepoint-queue',
]);

export const SYSTEM_SECTIONS = {
  approval: 1,
  users: 2,
  audits: 3,
  group: 4,
  application: 5,
  reporting: 7,
  audit: 8,
  auditReceipt: 9,
  auditNonCompliances: 10,
  auditPurchaseOrders: 11,
  auditAllAudits: 12,
  auditNotes: 13,
  cards: 14,
  suppliers: 15,
  auditPlanning: 16,
  auditsConfirmed: 17,
  calendar: 18,
  suppliersInvoicing: 19,
  countryManagement: 20,
  nonCompliances: 21,
  approvalHistory: 22,
  maintenance: 23,
  auditItinerary: 24,
  dashboard: 25,
  complaints: 26,
  ltqls: 27,
  specialDays: 28,
  raisedInvoices: 29,
  recommendations: 30,
  supplierRaisedInvoices: 31,
  maintenanceItineraryScheduleHeaders: 32,
  maintenanceItineraryScheduleItems: 33,
  maintenanceItinerarySamplingItems: 34,
  maintenanceAuditors: 35,
  maintenanceProductStandards: 36,
  maintenanceSchemes: 37,
  maintenanceLookupData: 38,
  emailInbox: 39,
  maintenanceMtrTemplates: 40,
  withdrawnCertificates: 41,
} as const;

export type PermissionAction = 'read' | 'add' | 'edit' | 'delete';
export type MaintenancePermissionSection =
  | 'itineraryscheduleheaders'
  | 'itineraryscheduleitems'
  | 'itinerarysamplingitems'
  | 'auditors'
  | 'productstandards'
  | 'schemes'
  | 'specialdays'
  | 'lookup-data'
  | 'mtr-templates';

export const MAINTENANCE_PERMISSION_ITEMS: ReadonlyArray<{
  section: MaintenancePermissionSection;
  sectionId: number;
  label: string;
  path: string;
}> = [
  {
    section: 'itineraryscheduleheaders',
    sectionId: SYSTEM_SECTIONS.maintenanceItineraryScheduleHeaders,
    label: 'Itinerary schedule headers',
    path: '/maintenance/itineraryscheduleheaders',
  },
  {
    section: 'itineraryscheduleitems',
    sectionId: SYSTEM_SECTIONS.maintenanceItineraryScheduleItems,
    label: 'Itinerary schedule items',
    path: '/maintenance/itineraryscheduleitems',
  },
  {
    section: 'itinerarysamplingitems',
    sectionId: SYSTEM_SECTIONS.maintenanceItinerarySamplingItems,
    label: 'Itinerary sampling items',
    path: '/maintenance/itinerarysamplingitems',
  },
  {
    section: 'auditors',
    sectionId: SYSTEM_SECTIONS.maintenanceAuditors,
    label: 'Auditors',
    path: '/maintenance/auditors',
  },
  {
    section: 'productstandards',
    sectionId: SYSTEM_SECTIONS.maintenanceProductStandards,
    label: 'Product Standards',
    path: '/maintenance/productstandards',
  },
  {
    section: 'schemes',
    sectionId: SYSTEM_SECTIONS.maintenanceSchemes,
    label: 'Schemes',
    path: '/maintenance/schemes',
  },
  {
    section: 'specialdays',
    sectionId: SYSTEM_SECTIONS.specialDays,
    label: 'Special Days',
    path: '/maintenance/specialdays',
  },
  {
    section: 'lookup-data',
    sectionId: SYSTEM_SECTIONS.maintenanceLookupData,
    label: 'Lookup Data',
    path: '/maintenance/lookup-data',
  },
  {
    section: 'mtr-templates',
    sectionId: SYSTEM_SECTIONS.maintenanceMtrTemplates,
    label: 'MTR Templates',
    path: '/maintenance/mtr-templates',
  },
];

const SUPPORTED_DEFAULT_ROUTES = new Set([
  '/approvals',
  '/withdrawn-certificates',
  '/users',
  '/audits',
  '/group',
  '/application',
  '/cards',
  '/audit/planning',
  '/audit/confirmed',
  '/audit/calendar',
  '/dashboard',
  '/complaints',
  '/ltqls',
  '/recommendations',
  '/country',
  '/reporting',
  '/raisedinvoices',
  '/noncompliance',
  '/suppliers',
  '/invoicing/suppliers',
  '/invoicing/customer-testing',
  '/maintenance/itineraryscheduleheaders',
  '/maintenance/itineraryscheduleitems',
  '/maintenance/itinerarysamplingitems',
  '/maintenance/auditors',
  '/maintenance/productstandards',
  '/maintenance/schemes',
  '/maintenance/specialdays',
  '/maintenance/lookup-data',
]);

const MAINTENANCE_DEFAULT_ROUTES = new Set([
  '/maintenance/itineraryscheduleheaders',
  '/maintenance/itineraryscheduleitems',
  '/maintenance/itinerarysamplingitems',
  '/maintenance/auditors',
  '/maintenance/productstandards',
  '/maintenance/schemes',
  '/maintenance/specialdays',
  '/maintenance/lookup-data',
]);

@Injectable({ providedIn: 'root' })
export class AuthService {
  private browser = inject(BrowserPlatformService);
  private readonly listingFilterState = inject(ListingFilterStateService);
  private launchPhases = inject(LaunchPhaseService);
  private authState = signal<UserLoginResponseDTO | null>(null);
  private sessionTimerId: ReturnType<typeof setTimeout> | null = null;
  private lastActivityRefreshAt = 0;
  private refreshRequest$: Observable<UserLoginResponseDTO> | null = null;
  private http: HttpClient;

  user = computed(() => this.authState());
  isImpersonating = computed(() => this.authState()?.isImpersonating === true);
  originalUserName = computed(() => this.authState()?.originalUserName ?? 'your account');
  returningToOriginal = signal(false);
  impersonationError = signal('');
  isAuthenticated = computed(() => {
    const auth = this.authState();

    return !!auth?.accessToken && !this.isExpired(auth.accessTokenExpiresAtUtc);
  });

  constructor(
    private router: Router,
    httpBackend: HttpBackend,
  ) {
    this.http = new HttpClient(httpBackend);
    this.clearLegacyStorage();
    this.startActivityTracking();
  }

  get token() {
    const auth = this.authState();

    return auth?.accessToken && !this.isExpired(auth.accessTokenExpiresAtUtc)
      ? auth.accessToken
      : null;
  }

  restoreSession() {
    return this.refreshSession().pipe(
      map(() => true),
      catchError(() => {
        this.clearSessionState();
        return of(false);
      }),
    );
  }

  prepareSessionForNewTab() {
    this.recordActivity();
  }

  setSession(auth: UserLoginResponseDTO) {
    this.listingFilterState.beginUserSession(auth.userId);
    this.authState.set(auth);
    this.lastActivityRefreshAt = Date.now();
    this.scheduleSessionTimeout();
  }

  logout(returnUrl?: string) {
    const sanitizedReturnUrl = returnUrl ? this.sanitizeReturnUrl(returnUrl) : null;

    this.clearSessionState();
    this.http
      .post<void>(`${environment.apiBaseUrl}/account/logout`, {}, { withCredentials: true })
      .pipe(catchError(() => of(null)))
      .subscribe();
    this.router.navigate(['/login'], {
      queryParams: sanitizedReturnUrl ? { returnUrl: sanitizedReturnUrl } : undefined,
    });
  }

  returnToOriginalUser() {
    if (!this.isImpersonating() || this.returningToOriginal()) return;

    this.returningToOriginal.set(true);
    this.impersonationError.set('');
    this.http
      .post<UserLoginResponseDTO>(
        `${environment.apiBaseUrl}/admin-users/return-to-original`,
        {},
        { withCredentials: true },
      )
      .subscribe({
        next: (session) => {
          this.setSession(session);
          this.returningToOriginal.set(false);
          void this.router.navigate([this.defaultRoute()]);
        },
        error: () => {
          this.returningToOriginal.set(false);
          this.impersonationError.set('Unable to return to your original session.');
        },
      });
  }

  recordActivity() {
    const auth = this.authState();

    if (!auth?.accessToken) return;

    const now = Date.now();
    if (now - this.lastActivityRefreshAt < ACTIVITY_REFRESH_INTERVAL_MS) return;

    this.lastActivityRefreshAt = now;
    this.refreshSession().subscribe({
      error: () => this.logout(this.router.url),
    });
  }

  refreshSession() {
    if (this.refreshRequest$) return this.refreshRequest$;

    this.refreshRequest$ = this.http
      .post<UserLoginResponseDTO>(
        `${environment.apiBaseUrl}/account/refresh-session`,
        {},
        { withCredentials: true },
      )
      .pipe(
        timeout(SESSION_REFRESH_TIMEOUT_MS),
        tap((res) => this.setSession(res)),
        finalize(() => (this.refreshRequest$ = null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.refreshRequest$;
  }

  handleUnauthorized(returnUrl?: string) {
    this.logout(returnUrl ?? this.router.url);
  }

  private clearSessionState() {
    this.listingFilterState.clear();
    this.authState.set(null);
    this.returningToOriginal.set(false);
    this.impersonationError.set('');
    this.lastActivityRefreshAt = 0;
    this.clearLegacyStorage();

    if (this.sessionTimerId) {
      clearTimeout(this.sessionTimerId);
      this.sessionTimerId = null;
    }
  }

  defaultRoute() {
    const auth = this.authState();

    if (!auth) return '/login';

    const defaultPermission = auth.permissions?.find(
      (permission) => permission.sectionId === auth.defaultSectionId,
    );
    const candidates = [
      this.toAngularRoute(auth.defaultRedirectUrl, auth.defaultSectionId),
      defaultPermission
        ? this.toAngularRoute(defaultPermission.redirectUrl, defaultPermission.sectionId)
        : null,
      this.routeForSection(auth.defaultSectionId),
      ...(auth.permissions ?? [])
        .filter((permission) => this.permissionAllows(permission, 'read'))
        .map((permission) => this.toAngularRoute(permission.redirectUrl, permission.sectionId)),
      ...(auth.menuSections ?? []).map((section) =>
        this.toAngularRoute(section.redirectUrl, section.sectionId),
      ),
    ];

    return candidates.find((route) => this.isAllowedDefaultRoute(route)) ?? '/group';
  }

  canOpenRouteForLaunchPhase(route: string) {
    const normalizedRoute = this.normalizeRoute(route).toLowerCase();

    if (
      (normalizedRoute === '/admin-tools' || normalizedRoute.startsWith('/admin-tools/')) &&
      this.canUseAdminTool(route)
    ) {
      return true;
    }

    return this.launchPhases.canOpenRoute(route, this.authState()?.isDcs === true);
  }

  canUseAdminTools() {
    return this.canUseAdminTool();
  }

  canUseAdminTool(route?: string) {
    if (this.launchPhases.canUseAdminTools(this.authState()?.isDcs === true)) {
      return true;
    }

    if (!this.isLimitedAdminToolsUser()) {
      return false;
    }

    if (!route || this.normalizeRoute(route).toLowerCase() === '/admin-tools') {
      return true;
    }

    return LIMITED_ADMIN_TOOL_ROUTES.has(this.normalizeRoute(route).toLowerCase());
  }

  canReadMenuItem(name: string, path?: string) {
    const permissions = this.authState()?.permissions ?? [];

    if (!permissions.length) return false;

    const itemNames = this.menuNameAliases(name);

    return permissions.some((section) => {
      if (!this.permissionAllows(section, 'read')) return false;

      const sectionTitle = this.normalizeMenuName(section.sectionTitle);
      const sectionRoute = this.normalizeRoute(this.toAngularRoute(section.redirectUrl));
      const itemRoute = path ? this.normalizeRoute(path) : '';

      return (
        itemNames.some(
          (itemName) =>
            sectionTitle === itemName ||
            sectionTitle.includes(itemName) ||
            itemName.includes(sectionTitle),
        ) ||
        (!!itemRoute &&
          (itemRoute === sectionRoute || this.sameRouteFamily(itemRoute, sectionRoute)))
      );
    });
  }

  permissionForSection(sectionId: number) {
    return (
      this.authState()?.permissions?.find((permission) => permission.sectionId === sectionId) ??
      null
    );
  }

  hasSectionPermission(sectionId: number, action: PermissionAction) {
    const permission = this.permissionForSection(sectionId);

    if (!permission) return false;

    return this.permissionAllows(permission, action);
  }

  hasMaintenanceSectionPermission(
    section: MaintenancePermissionSection | string | null | undefined,
    action: PermissionAction,
  ) {
    const item = this.maintenancePermissionItem(section);

    if (item) {
      const permission = this.permissionForSection(item.sectionId);

      if (permission) {
        return this.permissionAllows(permission, action);
      }

      // MTR Templates was introduced with its own permission row. Never expose
      // it through the legacy parent-Maintenance fallback when that row is absent.
      if (item.sectionId === SYSTEM_SECTIONS.maintenanceMtrTemplates) {
        return false;
      }
    }

    return this.hasSectionPermission(SYSTEM_SECTIONS.maintenance, action);
  }

  canReadMaintenanceSection(section: MaintenancePermissionSection | string | null | undefined) {
    return this.hasMaintenanceSectionPermission(section, 'read');
  }

  canReadSection(sectionId: number) {
    return this.hasSectionPermission(sectionId, 'read');
  }

  private isExpired(expiresAtUtc?: string | null) {
    if (!expiresAtUtc) return true;

    return new Date(expiresAtUtc).getTime() <= Date.now();
  }

  private isLimitedAdminToolsUser() {
    return (
      this.authState()?.userEmailAddress?.trim().toLowerCase() === LIMITED_ADMIN_TOOLS_EMAIL
    );
  }

  private scheduleSessionTimeout() {
    if (this.sessionTimerId) {
      clearTimeout(this.sessionTimerId);
      this.sessionTimerId = null;
    }

    const expiresAt = this.authState()?.accessTokenExpiresAtUtc;
    if (!expiresAt) return;

    const delay = Math.max(new Date(expiresAt).getTime() - Date.now(), 0);
    this.sessionTimerId = setTimeout(() => this.logout(this.router.url), delay);
  }

  private startActivityTracking() {
    const events = ['click', 'keydown', 'pointerdown', 'submit', 'focus'];

    for (const eventName of events) {
      this.browser.addWindowEventListener(eventName, () => this.recordActivity(), {
        passive: true,
      });
    }

    this.browser.addDocumentEventListener('visibilitychange', () => {
      if (!this.browser.document?.hidden) {
        this.recordActivity();
      }
    });
  }

  private clearLegacyStorage() {
    this.browser.removeLocalStorageItem(AUTH_STORAGE_KEY);
    this.browser.removeLocalStorageItem(AUTH_TAB_HANDOFF_STORAGE_KEY);
    this.browser.removeSessionStorageItem(AUTH_STORAGE_KEY);
  }

  private sanitizeReturnUrl(returnUrl: string) {
    if (
      returnUrl === '/' ||
      !returnUrl.startsWith('/') ||
      returnUrl.startsWith('//') ||
      returnUrl.startsWith('/login')
    ) {
      return null;
    }

    return returnUrl;
  }

  private normalizeRoute(route: string) {
    const path = route.split('?')[0].split('#')[0];

    return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
  }

  private sameRouteFamily(requestedRoute: string, allowedRoute: string) {
    return (
      (requestedRoute.startsWith('/group') && allowedRoute.startsWith('/group')) ||
      (requestedRoute.startsWith('/audits') && allowedRoute.startsWith('/audits')) ||
      (requestedRoute.startsWith('/cards') && allowedRoute.startsWith('/cards')) ||
      (requestedRoute.startsWith('/audit') && allowedRoute.startsWith('/audit')) ||
      (requestedRoute.startsWith('/application') && allowedRoute.startsWith('/application')) ||
      (requestedRoute.startsWith('/recommendations') &&
        allowedRoute.startsWith('/recommendations')) ||
      (requestedRoute.startsWith('/country') && allowedRoute.startsWith('/country')) ||
      (requestedRoute.startsWith('/reporting') && allowedRoute.startsWith('/reporting')) ||
      (requestedRoute.startsWith('/suppliers') && allowedRoute.startsWith('/suppliers')) ||
      (requestedRoute.startsWith('/invoicing') && allowedRoute.startsWith('/invoicing')) ||
      (requestedRoute.startsWith('/ltqls') && allowedRoute.startsWith('/ltqls'))
    );
  }

  private menuNameAliases(itemName: string) {
    const normalizedName = this.normalizeMenuName(itemName);
    const aliases: Record<string, string[]> = {
      all: ['all audits', 'audits'],
      tbp: ['tbp', 'to be planned', 'audit planning', 'audits to be planned'],
      cwc: ['cwc', 'confirmed work', 'confirmed work confirmed', 'audit confirmed'],
      calendar: ['calendar', 'audit calendar', 'planning calendar'],
      customers: ['customers', 'customer', 'group', 'groups'],
      applications: ['applications', 'application'],
      recommendations: ['recommendations', 'recommendation'],
      reporting: ['reporting', 'reports', 'report'],
      'travel advice': ['travel advice', 'country management', 'country'],
      'raised invoices': ['raised invoices', 'raised invoice'],
      ltqls: ['ltqls', 'ltql'],
      audits: ['audits', 'audit'],
      suppliers: ['suppliers', 'supplier'],
      'executive dashboard': ['executive dashboard', 'dashboard'],
      'suppliers invoicing': ['suppliers invoicing', 'supplier invoices', 'suppliers invoices'],
      'itinerary schedule headers': ['itinerary schedule headers', 'itinerary schedule header'],
      'itinerary schedule items': ['itinerary schedule items', 'itinerary schedule item'],
      'itinerary sampling items': [
        'itinerary sampling items',
        'itinerary sampling item',
        'iterinary sampling items',
        'iterinary sampling item',
      ],
      auditors: ['auditors', 'auditor'],
      'product standards': ['product standards', 'product standard'],
      schemes: ['schemes', 'scheme'],
      'special days': ['special days', 'special day'],
    };

    return [
      ...new Set([
        normalizedName,
        this.singularizeName(normalizedName),
        ...(aliases[normalizedName] ?? []).map((alias) => this.normalizeMenuName(alias)),
      ]),
    ];
  }

  private normalizeMenuName(value: string) {
    return value
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private singularizeName(value: string) {
    return value
      .split(' ')
      .map((word) => (word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word))
      .join(' ');
  }

  private toAngularRoute(redirectUrl: string | null, sectionId?: number | null) {
    if (!redirectUrl) return '/';

    const url = this.normalizeLegacyRoute(redirectUrl);

    if (url === '/audit/planning') return '/audit/planning';
    if (url === '/audit/confirmed') return '/audit/confirmed';
    if (url === '/audit/calendar') return '/audit/calendar';
    if (MAINTENANCE_DEFAULT_ROUTES.has(url)) return url;

    if (url.includes('approval')) return '/approvals';
    if (url.includes('users')) return '/users';
    if (url.includes('group')) return '/group';
    if (url.includes('dashboard')) return '/dashboard';
    if (url.includes('planning')) return '/audit/planning';
    if (url.includes('confirmed')) return '/audit/confirmed';
    if (url.includes('receipt')) return '/audits';
    if (url.includes('noncompliance') || url.includes('non-compliance')) {
      return sectionId === SYSTEM_SECTIONS.nonCompliances ? '/noncompliance' : '/audits';
    }
    if (url.includes('purchase')) return '/audits';
    if (url.includes('all')) return '/audits';
    if (url.includes('notes')) return '/audits';
    if (url.includes('itinerary')) return '/audits';
    if (url.includes('audit')) return '/audits';
    if (url.includes('ptcards') || url.includes('cards')) return '/cards';
    if (url.includes('application')) return '/application';
    if (url.includes('recommendation')) return '/recommendations';
    if (url.includes('country')) return '/country';
    if (url.includes('reporting') || url.includes('report')) return '/reporting';
    if (url.includes('complaint')) return '/complaints';
    if (
      url.includes('customertesting') ||
      url.includes('customer-testing') ||
      url.includes('supplierraisedinvoice') ||
      url.includes('supplier-raised-invoice')
    ) {
      return '/invoicing/customer-testing';
    }
    if (url.includes('raisedinvoice') || url.includes('raised-invoice')) return '/raisedinvoices';
    if (url.includes('ltql')) return '/ltqls';
    if (url === '/suppliers' || url.startsWith('/suppliers/')) return '/suppliers';
    if (url.includes('invoicing') && url.includes('supplier')) return '/invoicing/suppliers';
    if (url === '/maintenance') return '/maintenance/itineraryscheduleheaders';

    return this.routeForSection(sectionId) ?? url;
  }

  private routeForSection(sectionId?: number | null) {
    const maintenanceItem = MAINTENANCE_PERMISSION_ITEMS.find(
      (item) => item.sectionId === sectionId,
    );

    if (maintenanceItem) {
      return maintenanceItem.path;
    }

    switch (sectionId) {
      case SYSTEM_SECTIONS.approval:
      case SYSTEM_SECTIONS.approvalHistory:
        return '/approvals';
      case SYSTEM_SECTIONS.withdrawnCertificates:
        return '/withdrawn-certificates';
      case SYSTEM_SECTIONS.users:
        return '/users';
      case SYSTEM_SECTIONS.audits:
      case SYSTEM_SECTIONS.audit:
      case SYSTEM_SECTIONS.auditReceipt:
      case SYSTEM_SECTIONS.auditNonCompliances:
      case SYSTEM_SECTIONS.auditPurchaseOrders:
      case SYSTEM_SECTIONS.auditAllAudits:
      case SYSTEM_SECTIONS.auditNotes:
      case SYSTEM_SECTIONS.auditItinerary:
        return '/audits';
      case SYSTEM_SECTIONS.group:
        return '/group';
      case SYSTEM_SECTIONS.application:
        return '/application';
      case SYSTEM_SECTIONS.reporting:
        return '/reporting';
      case SYSTEM_SECTIONS.cards:
        return '/cards';
      case SYSTEM_SECTIONS.suppliers:
        return '/suppliers';
      case SYSTEM_SECTIONS.auditPlanning:
        return '/audit/planning';
      case SYSTEM_SECTIONS.auditsConfirmed:
        return '/audit/confirmed';
      case SYSTEM_SECTIONS.calendar:
        return '/audit/calendar';
      case SYSTEM_SECTIONS.maintenance:
        return '/maintenance/itineraryscheduleheaders';
      case SYSTEM_SECTIONS.dashboard:
        return '/dashboard';
      case SYSTEM_SECTIONS.complaints:
        return '/complaints';
      case SYSTEM_SECTIONS.ltqls:
        return '/ltqls';
      case SYSTEM_SECTIONS.recommendations:
        return '/recommendations';
      case SYSTEM_SECTIONS.countryManagement:
        return '/country';
      case SYSTEM_SECTIONS.raisedInvoices:
        return '/raisedinvoices';
      case SYSTEM_SECTIONS.supplierRaisedInvoices:
        return '/invoicing/customer-testing';
      case SYSTEM_SECTIONS.nonCompliances:
        return '/noncompliance';
      case SYSTEM_SECTIONS.suppliersInvoicing:
        return '/invoicing/suppliers';
      default:
        return null;
    }
  }

  private isSupportedDefaultRoute(route: string | null | undefined) {
    if (!route) return false;

    return SUPPORTED_DEFAULT_ROUTES.has(this.normalizeRoute(route).toLowerCase());
  }

  private isAllowedDefaultRoute(route: string | null | undefined) {
    if (!route) return false;

    const normalizedRoute = this.normalizeRoute(route).toLowerCase();
    const maintenanceItem = MAINTENANCE_PERMISSION_ITEMS.find(
      (item) => item.path === normalizedRoute,
    );

    if (maintenanceItem && !this.canReadMaintenanceSection(maintenanceItem.section)) {
      return false;
    }

    return this.isSupportedDefaultRoute(route) && this.canOpenRouteForLaunchPhase(route);
  }

  private maintenancePermissionItem(
    section: MaintenancePermissionSection | string | null | undefined,
  ) {
    const normalized = (section ?? '').trim().toLowerCase();
    const canonical =
      normalized === 'iterinarysamplingitems' ? 'itinerarysamplingitems' : normalized;

    return MAINTENANCE_PERMISSION_ITEMS.find((item) => item.section === canonical) ?? null;
  }

  private normalizeLegacyRoute(route: string) {
    const path = route.trim().toLowerCase();
    const angularRoute = path.startsWith('/') ? path : `/${path}`;

    return this.normalizeRoute(angularRoute);
  }

  private permissionAllows(
    permission: {
      canRead: boolean;
      canEdit: boolean;
      canAdd: boolean;
      canDelete: boolean;
    },
    action: PermissionAction,
  ) {
    switch (action) {
      case 'read':
        return (
          permission.canRead || permission.canEdit || permission.canAdd || permission.canDelete
        );
      case 'add':
        return permission.canAdd;
      case 'edit':
        return permission.canEdit;
      case 'delete':
        return permission.canDelete;
      default:
        return false;
    }
  }
}
